import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Lock, Eye, EyeOff } from "lucide-react";

function fmtErr(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ") || fallback;
  return fallback;
}

function PasswordInput({ value, onChange, placeholder, required, minLength, autoFocus, testId }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        className="input-base pr-11"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        minLength={minLength}
        autoFocus={autoFocus}
        data-testid={testId}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-white/5 transition"
        style={{ color: "var(--text-muted)" }}
        aria-label={show ? "Hide password" : "Show password"}
        tabIndex={-1}
        data-testid={`${testId}-toggle`}
      >
        {show ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  );
}

export default function Login() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const linkParams = new URLSearchParams(window.location.search);
  const claimToken = linkParams.get("claim");
  const resetToken = linkParams.get("reset");
  const [phase, setPhase] = useState(claimToken || resetToken ? "create" : "email"); // email | password | create | sent
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [info, setInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  // A link in hand already proves the email, so fetch it instead of asking for it again.
  useEffect(() => {
    const token = claimToken || resetToken;
    if (!token) return;
    api
      .get("/auth/link-info", { params: { token, purpose: claimToken ? "claim" : "reset" } })
      .then(({ data }) => {
        setEmail(data.email || "");
        setInfo({ name: data.name, has_password: !!resetToken });
      })
      .catch((err) => {
        toast.error(fmtErr(err, "That link is no longer valid"));
        setPhase("email");
      });
  }, [claimToken, resetToken]);

  useEffect(() => {
    if (!loading && user) {
      navigate(user.is_admin ? "/admin" : "/dashboard", { replace: true });
    }
  }, [user, loading, navigate]);

  const checkEmail = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/check", { email });
      setInfo(data);
      if (data.has_password) {
        setPhase("password");
      } else {
        // New account: the email must be verified before a password can be set.
        await api.post("/auth/send-link", { email });
        setPhase("sent");
      }
    } catch (err) {
      toast.error(fmtErr(err, "Couldn't verify that email"));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (phase === "create") {
      if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
      if (password !== confirm) { toast.error("Passwords don't match"); return; }
    }
    setBusy(true);
    try {
      const { data } = resetToken
        ? await api.post("/auth/reset-password", { link_token: resetToken, password })
        : await api.post("/auth/login", {
            email,
            password,
            name: name || undefined,
            link_token: claimToken || undefined,
          });
      login(data.token, data.user);
      toast.success(`Welcome${data.user.name ? `, ${data.user.name.split(" ")[0]}` : ""}`);
      navigate(data.user.is_admin ? "/admin" : "/dashboard", { replace: true });
    } catch (err) {
      toast.error(fmtErr(err, "Sign-in failed"));
    } finally {
      setBusy(false);
    }
  };

  const sendLink = async () => {
    if (!email) { toast.error("Enter your email first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/send-link", { email });
      toast.success(data.sent === "reset" ? "Reset link sent. Check your email." : "Setup link sent. Check your email.");
      setPhase("sent");
    } catch (err) {
      toast.error(fmtErr(err, "Couldn't send that link"));
    } finally {
      setBusy(false);
    }
  };

  const back = () => {
    setPhase("email");
    setPassword("");
    setConfirm("");
    setInfo(null);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="login-page">
      <div className="w-full max-w-md fade-up">
        <div className="surface-card p-8 sm:p-10">
          <div className="flex flex-col items-center mb-1">
            <img
              src="/wu_logo_full.png"
              alt="Wholesalers University"
              className="wu-brand-logo h-80 w-auto -mt-10 -mb-6"
              data-testid="brand-link"
            />
            <div
              className="font-mono text-[10px] uppercase tracking-[0.25em] mb-6"
              style={{ color: "var(--lime)" }}
              data-testid="login-tagline"
            >
              Student Portal
            </div>
          </div>

          {phase === "email" && (
            <>
              <h1 className="h-display text-2xl mb-2 text-center">Sign in</h1>
              <p className="text-sm mb-7 text-center" style={{ color: "var(--text-muted)" }}>
                Enter the email you enrolled with.
              </p>
              <form onSubmit={checkEmail} className="space-y-5">
                <div>
                  <label className="field-label">Email <span className="req">*</span></label>
                  <input
                    type="email"
                    className="input-base"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    data-testid="login-email-input"
                  />
                </div>
                <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2" disabled={busy} data-testid="login-continue-button">
                  {busy ? <span className="spinner" /> : <>Continue <ArrowRight size={16} /></>}
                </button>
              </form>
            </>
          )}

          {phase === "password" && (
            <>
              <h1 className="h-display text-2xl mb-2 text-center">
                Welcome back{info?.name ? `, ${info.name.split(" ")[0]}` : ""}
              </h1>
              <p className="text-sm mb-7 text-center font-mono" style={{ color: "var(--text-muted)" }}>{email}</p>
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label className="field-label">Password <span className="req">*</span></label>
                  <PasswordInput
                    placeholder="Your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoFocus
                    testId="login-password-input"
                  />
                </div>
                <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2" disabled={busy} data-testid="login-submit-button">
                  {busy ? <span className="spinner" /> : <><Lock size={14} /> Sign in</>}
                </button>
              </form>
              <p className="text-xs mt-5 text-center" style={{ color: "var(--text-dim)" }}>
                Forgot your password?{" "}
                <button onClick={sendLink} disabled={busy} style={{ color: "var(--lime)" }} data-testid="send-reset-link">
                  Email me a reset link
                </button>
              </p>
            </>
          )}

          {phase === "sent" && (
            <div className="text-center" data-testid="login-link-sent">
              <h1 className="h-display text-2xl mb-2">Check your email</h1>
              <p className="text-sm mb-6" style={{ color: "var(--text-muted)" }}>
                We sent a one-time link to <span className="font-mono">{email}</span>. Tap it to set your password.
                It expires in 30 minutes.
              </p>
              <button onClick={sendLink} disabled={busy} className="btn-ghost text-sm" data-testid="resend-link">
                Send it again
              </button>
            </div>
          )}

          {phase === "create" && (
            <>
              <h1 className="h-display text-2xl mb-2 text-center">{resetToken ? "Choose a new password" : "Create your password"}</h1>
              <p className="text-sm mb-7 text-center font-mono" style={{ color: "var(--text-muted)" }}>{email}</p>
              <form onSubmit={submit} className="space-y-5">
                {!info?.name && !resetToken && (
                  <div>
                    <label className="field-label">Your name <span className="req">*</span></label>
                    <input
                      type="text"
                      className="input-base"
                      placeholder="e.g. Alex Carter"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      autoFocus
                      data-testid="login-name-input"
                    />
                  </div>
                )}
                <div>
                  <label className="field-label">Create password <span className="req">*</span> <span className="opt">(min 8 characters)</span></label>
                  <PasswordInput
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    testId="login-create-password-input"
                  />
                </div>
                <div>
                  <label className="field-label">Confirm password <span className="req">*</span></label>
                  <PasswordInput
                    placeholder="••••••••"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    testId="login-confirm-password-input"
                  />
                </div>
                <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2" disabled={busy} data-testid="login-create-submit-button">
                  {busy ? <span className="spinner" /> : <><Lock size={14} /> Create & sign in</>}
                </button>
              </form>
            </>
          )}

          {phase !== "email" && (
            <button onClick={back} className="mt-5 mx-auto flex items-center gap-1.5 text-xs font-mono" style={{ color: "var(--text-muted)" }} data-testid="login-back-button">
              <ArrowLeft size={12} /> Use a different email
            </button>
          )}

          <div className="mt-7 pt-5 text-center space-y-2" style={{ borderTop: "1px solid var(--border)" }}>
            <p className="text-xs" style={{ color: "var(--text-dim)" }}>
              Can&apos;t sign in?{" "}
              <a href="mailto:support@wholesalersuniversity.com" style={{ color: "var(--lime)" }} data-testid="login-support-link">
                Contact support
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
